<template>
  <div>
    <v-card height="100%" class="d-flex flex-column">
      <v-card-title>
        {{ editRequest === null ? 'Create' : 'Update' }} Ad Hoc Request {{ editRequest ? editRequest.id : '' }}
      </v-card-title>

      <v-card-text style="overflow:scroll">
        <v-form :disabled="isReadOnly">
          <v-row>
            <v-col cols="4">
              <inventory-class-select-element
                v-model="inventoryClassId"
                :is-required="true"
                :is-add-all="false" />
            </v-col>
            <v-col cols="4">
              <v-autocomplete v-model="jobId" label="Job" :items="jobs" :item-text="(item) => `${item.code} ${item.name}`" item-value="id" clearable :loading="jobsAreLoading || formIsLoading">
                <template #selection="{ item }">
                  <span style="font-weight: bold">{{ item.code ? `${item.code}` : '' }}</span> - {{ item.name ? item.name : '' }}
                  <span style="color: #1976d2">{{ item.reference ? `; ${item.reference}` : '' }}</span>
                </template>
                <template #label>
                  Job<RedAsterisk />
                </template>

                <template #item="{ item }">
                  <span style="font-weight: bold">{{ item.code ? `${item.code}` : '' }}</span> - {{ item.name ? item.name : '' }}
                  <span style="color: #1976d2">{{ item.reference ? `; ${item.reference}` : '' }}</span>
                </template>
                <template #append-outer>
                  <v-btn color="primary" outlined small @click="openJobForm()">
                    <v-icon> mdi-plus </v-icon>
                  </v-btn>
                </template>
              </v-autocomplete>
            </v-col>
            <v-col cols="4">
                <v-menu
                  ref="menuRequiredBy"
                  v-model="menuProductionDate"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template #activator="{ on, attrs }">
                    <v-text-field
                      v-model="productionDate"
                      readonly
                      v-bind="attrs"
                      append-icon="mdi-calendar"
                      v-on="on"
                      :loading="formIsLoading"
                      @change="$refs.menuRequiredBy.save(productionDate)"
                    >
                      <template #label>Panel Required Date<RedAsterisk/></template>
                    </v-text-field>
                  </template>
                  <v-date-picker v-model="productionDate" no-title scrollable :allowed-dates="productionDateAllowedDates">
                    <v-spacer />
                    <v-btn text color="primary" @click="menu = false">Cancel</v-btn>
                    <v-btn text color="primary" @click="productionDate = null">Clear</v-btn>
                  </v-date-picker>
                </v-menu>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" class="text-end">
              <v-btn v-if="!isReadOnly" color="primary" outlined @click="openItemForm()">
                <v-icon small class="mr-1">
                  fas fa-plus
                </v-icon>
                New Item
              </v-btn>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <v-data-table
                :headers="tableHeaders"
                :items="filteredRequestItems"
                :loading="formIsLoading"
                :items-per-page="-1"
                dense >
 
              <template #item="{ item, index }">
                <tr>
                  <td>{{ panelTypeMap[item.panel_type_id] }}</td>
                  <td>{{ item.tag_number }}</td>
                  <td>{{ item.length }}</td>
                  <td>{{ item.width }}</td>
                  <td>{{ item.height }}</td>
                  <td>{{ item.quantity }}</td>
                  <td>{{ colorMap[item.color_id] }}</td>
                  <td>{{ item.notes }}</td>
                  <!-- <td>
                    <a @click="openItemShowImageForm(item)"> 
                      <v-icon small color="primary">fa-image</v-icon> 
                    </a>
                  </td> -->
                  <td>
                    <v-chip
                      v-if="statusMap[item.status_id]"
                      small
                      style="width: 100%; max-width: 140px; white-space: nowrap; "
                      :color="statusMap[item.status_id].color"
                      text-color="white"
                    > 
                    {{ statusMap[item.status_id].name }}
                    </v-chip>
                  </td>
                  <td class="text-end">
                    <div v-if="!isReadOnly">
                      <v-btn color="primary" @click="openItemForm(index)" small outlined>Edit</v-btn>
                      <v-btn color="secondary" @click="cloneItem(item)" small outlined class="ma-1">Clone</v-btn>
                      <v-btn color="error" @click="deleteItem(item)" small outlined>Delete</v-btn >
                    </div>
                  </td>
                </tr>
              </template>
              </v-data-table>
            </v-col>
          </v-row>

          <v-row>
            <v-col>
              <v-card>
                <v-card-title>
                  <v-row>
                    <v-col>
                      <span style="font-size: 20px; font-weight: 500;">Attachments<RedAsterisk /></span>
                    </v-col>
                  </v-row>
                </v-card-title>
                <v-card-text>
                  <v-row>
                    <v-col class="pb-0">
                      <v-file-input v-model="attachmentInput" outlined small-chips>
                        <template slot="label">
                          Attachment
                        </template>
                      </v-file-input>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col class="text-end pt-0">
                      <v-btn v-if="!isReadOnly" color="primary" @click="addAttachment()" :disabled="this.attachmentInput === null">
                        ADD ATTACHMENT
                      </v-btn>
                    </v-col>
                  </v-row>
                  <div v-if="attachments.length > 0">
                    <v-row>
                      <v-col class="pb-0 pt-0 ">
                        <zig-zag />
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col>
                        <v-row>
                          <v-col>
                            <v-card-text style="max-height: 500px; overflow-y: auto; border: 1px solid #d3d3d3; border-radius: 3px;">
                              <v-list two-line class="pt-0 pb-0">
                                <div v-for="(file, index) in attachments" :key="'attachment_' + index">
                                  <v-list-item>
                                    <v-list-item-content>
                                      <v-img v-if="file.photo" :src="convertedPhoto(file.photo, file.mime_type)" contain max-width="500"></v-img>
                                      <span v-else>
                                        {{ file.original_file_name || file.file_name || file.name }}
                                      </span>
                                      <v-list-item-subtitle style="padding-top: 10px;">
                                        {{ file.created_at || "Not Saved" }}
                                      </v-list-item-subtitle>
                                      <v-list-item-subtitle v-if="file.user">
                                        {{ `${file.user.first_name ? file.user.first_name : ''} ${file.user.last_name ? file.user.last_name : ''}` }}
                                      </v-list-item-subtitle>
                                      <v-list-item-subtitle v-else>
                                        {{ user.first_name }} {{ user.last_name }}
                                      </v-list-item-subtitle>
                                      <v-list-item-subtitle class="text-end">
                                        <v-btn v-if="file.id" color="purple" class="mr-2" outlined small @click="downloadAttachment(file)">
                                          <v-icon small class="mr-1">fas fa-download</v-icon>
                                        </v-btn>
                                        <v-btn color="error" outlined small @click="deleteAttachment(index)">
                                          <v-icon small class="mr-1">fas fa-trash</v-icon>
                                        </v-btn>
                                      </v-list-item-subtitle>
                                    </v-list-item-content>
                                  </v-list-item>
                                  <v-divider v-if="index < attachments.length - 1"/>
                                </div>
                              </v-list>
                            </v-card-text>
                          </v-col>
                        </v-row>
                      </v-col>
                    </v-row>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
        <v-spacer />
      <v-card-actions>
        <v-spacer />
        <v-btn color="normal" class="mr-2" @click="closeForm">CLOSE</v-btn>
        <v-btn v-if="!isReadOnly" color="primary" class="mr-2" @click="saveRequest">{{ editRequest === null ? 'CREATE' : 'UPDATE' }}</v-btn>
      </v-card-actions>
    </v-card>

    <job-form
      ref="jobForm"
      :is-supervisor="false"
      :is-client="false"
      :is-work-order="false"
      :is-address="false"
      :is-job-name="false"
      :is-reference="true"
      @success="jobSuccess"
    />

    <v-dialog v-model="itemFormIsShown" persistent width="50%">
      <ad-hoc-request-item-form
        ref="requestItemForm"
        @item-created="onItemCreated"
        @item-updated="onItemUpdated"
        @close-form="closeItemForm"
      />
    </v-dialog>

    <confirm-form ref="confirmForm" />

    <InventoryItemShowImageForm ref="itemShowImageForm" />
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import JobForm from '../jobs/JobForm.vue';
import AdHocRequestItemForm from './AdHocRequestItemForm.vue';
import InventoryItemShowImageForm from '../InventoryItemShowImageForm.vue';
import * as adHocApi from '../../../api/WipPanelAdHocRequestApi';
import ZigZag from '@/components/elements/ZigZag';
import RedAsterisk from '../../RedAsterisk.vue';
export default {
  components: {
    JobForm,
    AdHocRequestItemForm,
    InventoryItemShowImageForm,
    ZigZag
  },
  data: () => ({
    formIsLoading: false,
    editId: null,
    editRequest: null,
    inventoryClassId: null,
    jobId: null,
    productionDate: null,
    jobs: [],
    jobsAreLoading: false,
    itemFormIsShown: false,
    menuProductionDate: false,
    panelTypeMap: {},
    colorMap: {},
    statusMap: {},
    currentDate: new Date(),

    attachmentInput: null,
    attachments: [],
    attachmentTypeIdAdHocPanelId: 9,

    tableHeaders: [
      { text: 'Panel Type', value: 'panel_type_id' },
      { text: 'Tag Number', value: 'tag_number' },
      { text: 'Length (inch)', value: 'length' },
      { text: 'Width (inch)', value: 'width' },
      { text: 'Height (inch)', value: 'height' },
      { text: 'Quantity', value: 'quantity' },
      { text: 'Color', value: 'color_id' },
      { text: 'Notes', value: 'notes' },
      // { text: 'Attachment', value: 'attachment_path' },
      { text: 'Status', value: 'status_id' },
      { text: 'Actions', value: 'actions', class: 'text-end' },
    ],
    requestItems: [],
  }),
  created() {
    this.getJobs();
    this.$api.color.read().then(res => {
      this.colorMap = res.reduce((acc, x) => {
        acc[x.id] = x.code;
        return acc;
      }, {});
    });

    this.$api.wipPanelType.read().then(res => {
      this.panelTypeMap = res.data.reduce((acc, x) => {
        acc[x.id] = x.code;
        return acc;
      }, {});
    });

    this.$api.glassPoStatus.get().then(res => {
      this.statusMap = res.reduce((acc, x) => {
        acc[x.id] = x;
        return acc;
      }, {});
    });
  },
  computed: {
    ...mapState({
      isDarkMode: 'isDarkMode',
      user: (state) => state.user,
    }),
    isAdmin() {
      return this.user.roles.some(x => x.id === 3 || x.id === 38);
    },
    ...mapGetters([
      'businessRulesGetBoolean',
    ]),
    isReadOnly() {
      return this.editRequest !== null && (this.editRequest.schedule_id !== null || (this.editRequest.requested_by_id !== this.user.id && !this.isAdmin));
    },
    filteredRequestItems() {
      return this.requestItems.filter(x => !x.is_deleted);
    }
  },
  watch: {
    inventoryClassId() {
      this.getJobs();
    },
  },
  methods: {
    async open(requestId = null) {
      this.resetForm();
      if (requestId !== null) {
        this.formIsLoading = true;
        this.editRequest = await adHocApi.getById(requestId);
        this.formIsLoading = false;
        this.editId = this.editRequest.id || null;
        this.inventoryClassId = this.editRequest.inventory_class_id;
        this.productionDate = this.editRequest.production_date;
        this.jobId = this.editRequest.job_id;
        this.requestItems = this.editRequest.request_items;

        this.attachments = await this.$api.attachment.read({
          filters: {
            record_id: requestId, 
            type_id: this.attachmentTypeIdAdHocPanelId,
          },
          includes: {
            is_include_item_image: true,
          },
        });
      }
    },
    async getJobs() {
      this.jobsAreLoading = true;
      if (this.inventoryClassId === null) {
        this.jobs = [];
      } else {
        const submitModel = {
          filters: { inventory_class_id: this.inventoryClassId },
        };
        this.jobs = await this.$api.job.readDDL(submitModel);
      }
      this.jobsAreLoading = false;
    },

    async openJobForm() {
      var res = null;
      if (this.businessRulesGetBoolean('Desa WO Codes')) {
        res = await this.$api.job.get(`generate-manual-code-desa/P`);
      } else {
        res = await this.$api.job.get('generate-manual-code');
      }
      
      if (!res) return;
      this.$refs.jobForm.open();
      this.$nextTick(() => {
        this.$refs.jobForm.item.code = res;
        this.$refs.jobForm.item.is_manual = 1;
        if (this.item.inventory_class_id) this.$refs.jobForm.item.inventory_class_id = this.item.inventory_class_id;
      });
    },
    async jobSuccess() {
      await this.getJobs();
      const latestItemId = Math.max(...this.jobs.map((x) => x.id));
      if (latestItemId) {
        this.item.job_id = latestItemId;
      }
    },
    openItemForm(itemIdx = null) {
      this.itemFormIsShown = true;
      const item = itemIdx === null ? null : this.requestItems[itemIdx];
      setTimeout(() => this.$refs.requestItemForm.open(item, itemIdx), 250);
    },
    cloneItem(item) {
      this.requestItems.push({ ...item, id: null, item_number: this.requestItems.length + 1 });
    },
    deleteItem(item) {
      item.is_deleted = 1;
    },
    closeItemForm() {
      this.itemFormIsShown = false; 
    },
    onItemCreated(item) {
      item.item_number = this.requestItems.length + 1;
      this.requestItems.push(item);
    }, 
    onItemUpdated(item, itemIdx) {
      // if (item.attachment === null) item.attachment = existingItem.attachment;
      this.requestItems[itemIdx].panel_type_id = item.panel_type_id;
      this.requestItems[itemIdx].length = item.length;
      this.requestItems[itemIdx].width = item.width;
      this.requestItems[itemIdx].height = item.height;
      this.requestItems[itemIdx].quantity = item.quantity;
      this.requestItems[itemIdx].color_id = item.color_id;
      this.requestItems[itemIdx].notes = item.notes;
      this.requestItems[itemIdx].tag_number = item.tag_number;
      // if (item.attachment !== null && typeof(item.attachment) === 'object') this.requestItems[existingIdx].attachment = item.attachment;
    },
    resetForm() {
      this.editRequest = null;
      this.editId = null;
      this.inventoryClassId = null;
      this.jobId = null;
      this.productionDate = null;
      this.requestItems = [];
      this.formIsLoading = false;
      this.attachments = [];
      this.attachment = null;
    },
    closeForm() {
      this.resetForm();
      this.$emit('close-form');
    },
    async saveRequest() {
      if (!this.inventoryClassId) return this.$bus.$emit('showError', 'Please select the Class');
      if (!this.jobId) return this.$bus.$emit('showError', 'Please select the Job');
      if (!this.productionDate) return this.$bus.$emit('showError', 'Please enter the Panel Required Date');
      if (this.requestItems.filter(x => !x.is_deleted).length === 0) return this.$bus.$emit('showError', 'Please add at least one item');
      if (this.attachments.length === 0) return this.$bus.$emit('showError', 'Please add at least one attachment');

      const data = new FormData();
      if (this.editId !== null) data.append('id', this.editId);
      data.append('inventory_class_id', this.inventoryClassId);
      data.append('job_id', this.jobId);
      data.append('production_date', this.productionDate);
      this.requestItems.forEach((item, i) => {
        if (item.id) data.append(`items[${i}][id]`, item.id);
        data.append(`items[${i}][item_number]`, item.item_number);
        data.append(`items[${i}][panel_type_id]`, item.panel_type_id);
        if (item.length !== null) data.append(`items[${i}][length]`, item.length);
        if (item.width !== null) data.append(`items[${i}][width]`, item.width);
        if (item.height !== null) data.append(`items[${i}][height]`, item.height);
        data.append(`items[${i}][quantity]`, item.quantity);
        data.append(`items[${i}][tag_number]`, item.tag_number);
        data.append(`items[${i}][color_id]`, item.color_id);
        data.append(`items[${i}][notes]`, item.notes || '');
        data.append(`items[${i}][is_deleted]`, item.is_deleted || 0);
      });


      try {
        const res = this.editId === null ? (await adHocApi.create(data)) : (await adHocApi.update(data));
        await this.saveAttachments(res.id);
        this.$bus.$emit('showSuccess', 'Ad Hoc Request has been saved successfully');
        this.closeForm();
      } catch (e) {
        console.error(e);
        this.$bus.$emit('showError', 'Failed to save the Ad Hoc Request');
      }
    },
    openItemShowImageForm(item) {
      if (typeof(item.attachment) === 'object') {
        this.$refs.itemShowImageForm.openDialogFromFile(item.attachment, '');
      } else {
        this.$refs.itemShowImageForm.openDialog(item.attachment, item.attachment_mime_type, '');
      }
    },
    productionDateAllowedDates(val) {
      const [year, month, day] = val.split('-').map(x => Number(x));
      const currentYear = this.currentDate.getFullYear();
      const currentMonth = this.currentDate.getMonth() + 1;
      const currentDay = this.currentDate.getDate();
      return year >= currentYear && month >= currentMonth && (month > currentMonth || (month === currentMonth && day > currentDay));
    },

    addAttachment() {
      if (this.attachmentInput === null) return;
      this.attachments.push(this.attachmentInput);
      this.attachmentInput = null;
    },

    async deleteAttachment(attachmentIdx) {
      const attachment = this.attachments[attachmentIdx];
      const confirm = await this.$refs.confirmForm.open({ title: 'Are you sure that you want to delete this attachment?' });
      if (!confirm) return;
      if (attachment.id) {
        const res = await this.$api.attachment.softDelete(attachment.id);
        if (!res) {
          this.$bus.$emit('showError');
          return;
        } else {
          this.$bus.$emit('showSuccess');
        }
      }
      this.$bus.$emit('showSuccess');
      this.attachments.splice(attachmentIdx, 1);
    },

    async downloadAttachment(file) {
      const res = await this.$api.attachment.get(`download/${file.id}`, { responseType: 'blob' });
      const downloadedFile = new Blob([res]);
      saveAs(downloadedFile, file.file_name);
    },

    convertedPhoto(photo, mimeType) {
      return `data:${mimeType};base64, ${photo}`;
    },

    async saveAttachments(id) {
      const attachmentsToSave = this.attachments.filter(x => !x.id);
      for (let i = 0; i < attachmentsToSave.length; i++) {
        const formData = new FormData();
        formData.append('file', attachmentsToSave[i]);
        formData.append('record_id', id);
        formData.append('type_id', this.attachmentTypeIdAdHocPanelId);
        const res = await this.$api.attachment.store(formData);
      }
    }
  },
};
</script>
